import Modal from './Modal';
import { useTranslation } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import './SettingsModal.scss';

/**
 * Settings modal with language selection, holiday management, and data import/export.
 * 
 * @param {Object} props
 * @param {Function} props.onClose - Handler for closing the modal
 * @param {Function} props.onManageHolidays - Handler for opening holiday management
 * @param {Function} props.onExportJSON - Handler for exporting JSON backup
 * @param {Function} props.onExportICS - Handler for opening iCalendar export
 * @param {Function} props.onImport - Handler for importing JSON backup
 */
function SettingsModal({ onClose, onManageHolidays, onExportJSON, onExportICS, onImport, onICalSettings }) {
  const { t, language, setLanguage, supportedLanguages } = useTranslation();
  const { isAuthenticated } = useAuth();

  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
  };

  const handleManageHolidays = () => {
    onManageHolidays();
  };

  return (
    <Modal 
      isOpen 
      onClose={onClose} 
      title={t('settings.title')}
      maxWidth={400}
    >
      <div className="settings-modal">
        {/* Language selection */}
        <div className="settings-modal__section">
          <label className="settings-modal__label" htmlFor="language-select">
            {t('settings.language')}
          </label>
          <select
            id="language-select"
            className="settings-modal__select"
            value={language}
            onChange={handleLanguageChange}
          >
            {supportedLanguages.map((lang) => (
              <option key={lang} value={lang}>
                {t(`settings.languages.${lang}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Holidays management button */}
        <div className="settings-modal__section">
          <button
            className="settings-modal__holidays-btn"
            onClick={handleManageHolidays}
          >
            {t('settings.manageHolidays')}
          </button>
        </div>

        {/* iCal Publishing (only for authenticated users) */}
        {isAuthenticated && onICalSettings && (
          <div className="settings-modal__section">
            <button
              className="settings-modal__holidays-btn"
              onClick={onICalSettings}
            >
              {t('ical.settingsButton')}
            </button>
          </div>
        )}

        {/* Data section divider */}
        <div className="settings-modal__divider">
          <span className="settings-modal__divider-text">{t('settings.data')}</span>
        </div>

        {/* Data import/export buttons */}
        <div className="settings-modal__section">
          <button
            className="settings-modal__data-btn"
            onClick={onExportJSON}
          >
            📤 {t('settings.exportJSON')}
          </button>
          {/* iCal export only for non-authenticated users (authenticated users have iCal publishing) */}
          {!isAuthenticated && (
            <button
              className="settings-modal__data-btn"
              onClick={onExportICS}
            >
              📅 {t('settings.exportICS')}
            </button>
          )}
          <button
            className="settings-modal__data-btn"
            onClick={onImport}
          >
            📥 {t('settings.importJSON')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default SettingsModal;
